// =====================================================================
// Génération de reçus de commande PDF pour Kassarmou Market
// - Téléchargeable en PDF (downloadReceiptPDF)
// - Imprimable (printReceiptPDF -> ouvre le PDF avec dialogue d'impression)
// Dépendances : jspdf, jspdf-autotable
// =====================================================================
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ---------------------------------------------------------------------
// Identité de la marque (à modifier ici uniquement)
// ---------------------------------------------------------------------
export const BRAND = {
  name: "KASSARMOU MARKET",
  tagline: "Votre marketplace de confiance",
  email: "support@kassarmoumarket.com",
  phone: "+32 465 96 54 36",
  website: "www.kassarmoumarket.com",
  address: "3053 Haasrode, Belgique",
  colors: {
    primary: [252, 145, 58], // Orange Kassarmou (#FC913A)
    primaryDark: [232, 126, 4], // Orange foncé (#E87E04)
    accent: [48, 160, 139], // Teal (#30A08B)
    dark: [45, 55, 72], // Texte principal (#2D3748)
    gray: [113, 128, 150], // Texte secondaire (#718096)
    light: [247, 250, 252], // Fond clair (#F7FAFC)
    border: [226, 232, 240], // Bordures (#E2E8F0)
    success: [40, 167, 69], // Vert (#28A745)
    warning: [255, 193, 7], // Jaune (#FFC107)
    danger: [220, 53, 69], // Rouge (#DC3545)
  },
};

// ---------------------------------------------------------------------
// Utilitaires de formatage
// ---------------------------------------------------------------------

// jsPDF (polices WinAnsi) ne gère pas les espaces insécables fines
// générées par Intl "fr-FR" -> on les remplace par des espaces normaux.
const cleanSpaces = (str) => String(str).replace(/[\u202F\u00A0]/g, " ");

const formatNumber = (value) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  return cleanSpaces(
    safe.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

const formatPrice = (value) => `${formatNumber(value)} €`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isColorUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value);

const getReceiptNumber = (order) =>
  String(
    order?.reference && order.reference !== "none"
      ? order.reference
      : (order?._id ?? "")
  );

const getReceiptFileName = (order) =>
  `Recu_Kassarmou_${getReceiptNumber(order).replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`;

// ---------------------------------------------------------------------
// Construction des lignes de produits pour le reçu
// Même logique que les emails (orderEmailService) et la page :
// 1) snapshot du panier (commande.prod) = prix réellement payé
// 2) variante / promo du produit en base
// 3) prix de base du produit
// ---------------------------------------------------------------------
export function buildOrderLines(order, products = []) {
  const snapshot = Array.isArray(order?.prod) ? order.prod : [];

  return (order?.nbrProduits || []).map((item) => {
    const productId = String(item?.produit ?? "");
    const product = products.find((p) => String(p?._id) === productId);

    // Ligne du panier correspondante (même produit + même variante)
    const snap = snapshot.find((s) => {
      if (!s || !s._id || String(s._id) !== productId) return false;
      const sameColor =
        !item.couleurs?.length ||
        !s.colors?.length ||
        s.colors[0] === item.couleurs[0];
      const sameSize =
        !item.tailles?.length ||
        !s.sizes?.length ||
        s.sizes[0] === item.tailles[0];
      return sameColor && sameSize;
    });

    // Prix réellement payé (promo / variante), avec replis successifs
    let unitPrice = Number(snap?.price);
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      const couleur = item?.couleurs?.[0];
      const variant =
        couleur && !isColorUrl(couleur)
          ? product?.variants?.find((v) => v?.color === couleur)
          : undefined;

      const normalPrice =
        variant?.price && variant.price > 0 ? variant.price : product?.prix || 0;
      const discountPrice =
        variant?.promoPrice && variant.promoPrice > 0
          ? variant.promoPrice
          : product?.prixPromo || 0;
      const isOnPromo =
        (discountPrice > 0 && discountPrice < normalPrice) || product?.isOnPromo;

      unitPrice = isOnPromo && discountPrice > 0 ? discountPrice : normalPrice;
    }

    const tailles = (item?.tailles || []).join(", ");
    const couleurs = (item?.couleurs || [])
      .filter((c) => !isColorUrl(c))
      .join(", ");

    return {
      nom: snap?.name || product?.name || "Produit",
      quantite: item?.quantite || 0,
      tailles: tailles || "-",
      couleurs: couleurs || "-",
      prixUnitaire: unitPrice,
      total: unitPrice * (item?.quantite || 0),
    };
  });
}

// ---------------------------------------------------------------------
// Blocs de dessin
// ---------------------------------------------------------------------
function drawBrandHeader(doc) {
  const colors = BRAND.colors;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 96, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.text(BRAND.name, 40, 42);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(BRAND.tagline, 40, 60);

  doc.text(BRAND.website, pageWidth - 40, 42, { align: "right" });
  doc.text(BRAND.email, pageWidth - 40, 56, { align: "right" });

  doc.setTextColor(...colors.dark);
}

function drawInfoBox(doc, { x, y, width, height, title, rows }) {
  const colors = BRAND.colors;

  doc.setFillColor(...colors.light);
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, width, height, 6, 6, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...colors.accent);
  doc.text(title, x + 12, y + 20);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  let cursorY = y + 38;

  rows.forEach(([label, value]) => {
    const text = value === undefined || value === null || value === "" ? "-" : value;
    const lines = doc.splitTextToSize(String(text), width - 110);

    doc.setTextColor(...colors.gray);
    doc.text(String(label), x + 12, cursorY);
    doc.setTextColor(...colors.dark);
    doc.text(lines, x + 100, cursorY);
    cursorY += Math.max(16, lines.length * 11 + 6);
  });

  return cursorY;
}

const statusColor = (status, colors) => {
  const s = String(status || "").toLowerCase();
  if (["payé", "paye", "recu", "livré", "livre", "traité", "traite"].includes(s)) {
    return colors.success;
  }
  if (["échec", "echec", "annulé", "annule"].includes(s)) return colors.danger;
  if (["en cours", "en route", "traitement"].includes(s)) return colors.warning;
  return colors.gray;
};

function drawStatusStrip(doc, x, y, width, order) {
  const colors = BRAND.colors;
  const colWidth = width / 3;

  doc.setFillColor(...colors.light);
  doc.setDrawColor(...colors.border);
  doc.roundedRect(x, y, width, 52, 6, 6, "FD");

  const items = [
    ["Statut paiement", order?.statusPayment],
    ["Statut livraison", order?.statusLivraison],
    ["État de traitement", order?.etatTraitement],
  ];

  items.forEach(([label, value], index) => {
    const colX = x + colWidth * index + 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...colors.gray);
    doc.text(label, colX, y + 18);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...statusColor(value, colors));
    doc.text(String(value ?? "-"), colX, y + 36);
  });

  doc.setTextColor(...colors.dark);
  return y + 52;
}

function drawTotals(doc, rightX, startY, rows) {
  const colors = BRAND.colors;
  let y = startY;

  rows.forEach((row) => {
    const [label, value, options = {}] = row;

    if (options.isTotal) {
      doc.setFillColor(...colors.accent);
      doc.roundedRect(rightX - 250, y - 14, 250, 28, 4, 4, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(label, rightX - 238, y + 4);
      doc.text(value, rightX - 12, y + 4, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 36;
    } else {
      doc.setFont("helvetica", options.isBold ? "bold" : "normal");
      doc.setFontSize(10);
      doc.setTextColor(...colors.gray);
      doc.text(label, rightX - 250, y);
      doc.setTextColor(...colors.dark);
      doc.text(value, rightX - 12, y, { align: "right" });
      doc.setFont("helvetica", "normal");
      y += 18;
    }
  });

  doc.setTextColor(...colors.dark);
  return y;
}

// ---------------------------------------------------------------------
// Construction du reçu PDF
// ---------------------------------------------------------------------
export function buildReceiptPDF({
  order,
  user,
  address,
  promoCode,
  products = [],
}) {
  const colors = BRAND.colors;
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const contentWidth = pageWidth - marginX * 2;

  // ===== En-tête de marque =====
  drawBrandHeader(doc);

  // ===== Titre + numéro de reçu =====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...colors.dark);
  doc.text("RECU DE COMMANDE", marginX, 132);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...colors.gray);
  doc.text(`N° : ${getReceiptNumber(order) || "-"}`, pageWidth - marginX, 126, {
    align: "right",
  });
  doc.text(
    `Émis le : ${formatDate(order?.paymentDetails?.paymentDate || order?.date)}`,
    pageWidth - marginX,
    142,
    { align: "right" }
  );

  // ===== Blocs client / adresse =====
  const boxTop = 158;
  const boxWidth = (contentWidth - 20) / 2;
  const clientEnd = drawInfoBox(doc, {
    x: marginX,
    y: boxTop,
    width: boxWidth,
    height: 138,
    title: "Information client",
    rows: [
      ["Nom :", user?.name || order?.livraisonDetails?.customerName],
      ["Email :", user?.email || order?.livraisonDetails?.email],
      [
        "Téléphone :",
        address?.numero ||
          order?.livraisonDetails?.numero ||
          order?.livraisonDetails?.telephone,
      ],
    ],
  });

  drawInfoBox(doc, {
    x: marginX + boxWidth + 20,
    y: boxTop,
    width: boxWidth,
    height: 138,
    title: "Adresse de livraison",
    rows: [
      ["Destinataire :", order?.livraisonDetails?.customerName || user?.name],
      ["Ville / Commune :", address?.quartier || order?.livraisonDetails?.quartier],
      ["Pays / Région :", address?.region || order?.livraisonDetails?.region],
      ["Code postal :", address?.codePost],
      [
        "Instructions :",
        address?.description || order?.livraisonDetails?.description,
      ],
    ],
  });

  // ===== Bandeau des statuts =====
  const stripY = Math.max(clientEnd, boxTop + 138) + 14;
  const stripEnd = drawStatusStrip(doc, marginX, stripY, contentWidth, order);

  // ===== Tableau des produits =====
  const lines = buildOrderLines(order, products);
  const sousTotal = lines.reduce((sum, l) => sum + l.total, 0);
  const reduction = Number(order?.reduction) || 0;
  const fraisLivraison =
    Number(order?.shippingDetails?.coutTotal ?? order?.shipping?.coutTotal) || 0;
  const totalCommande =
    Number(order?.prixTotal) ||
    Number(order?.prix) ||
    sousTotal - reduction + fraisLivraison;

  autoTable(doc, {
    startY: stripEnd + 18,
    margin: { left: marginX, right: marginX, top: 110 },
    head: [["Produit", "Qté", "Tailles", "Couleurs", "Prix unitaire", "Total"]],
    body: lines.map((l) => [
      l.nom,
      String(l.quantite),
      l.tailles,
      l.couleurs,
      formatPrice(l.prixUnitaire),
      formatPrice(l.total),
    ]),
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 6,
      textColor: colors.dark,
      lineColor: colors.border,
      lineWidth: 0.7,
    },
    headStyles: {
      fillColor: colors.accent,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9.5,
    },
    alternateRowStyles: { fillColor: [252, 253, 254] },
    columnStyles: {
      0: { cellWidth: 175 },
      1: { halign: "center", cellWidth: 38 },
      2: { halign: "center", cellWidth: 62 },
      3: { cellWidth: 80 },
      4: { halign: "right" },
      5: { halign: "right", fontStyle: "bold" },
    },
    didDrawPage: (data) => {
      // Ré-affiche l'en-tête de marque sur les pages suivantes
      if (data.pageNumber > 1) {
        drawBrandHeader(doc);
      }
    },
  });

  let cursorY = (doc.lastAutoTable?.finalY || stripEnd + 60) + 26;

  // Passage à la page suivante si le bloc totaux ne tient pas
  if (cursorY > pageHeight - 190) {
    doc.addPage();
    cursorY = 130;
  }

  // ===== Bloc totaux =====
  const totalsRows = [["Sous-total produits", formatPrice(sousTotal)]];
  if (order?.codePro && reduction > 0) {
    totalsRows.push([
      `Réduction${promoCode?.code ? ` (code : ${promoCode.code})` : ""}`,
      `- ${formatPrice(reduction)}`,
    ]);
  }
  totalsRows.push(["Frais de livraison", formatPrice(fraisLivraison)]);
  totalsRows.push(["TOTAL COMMANDÉ", formatPrice(totalCommande), { isTotal: true }]);

  cursorY = drawTotals(doc, pageWidth - marginX, cursorY, totalsRows);

  // ===== Informations de paiement =====
  const paymentRows = [
    ["Date de paiement :", formatDate(order?.paymentDetails?.paymentDate)],
    ["Référence commande :", getReceiptNumber(order) || "-"],
  ];
  if (order?.paymentDetails?.paymentIntentId) {
    paymentRows.push(["Payment Intent ID :", order.paymentDetails.paymentIntentId]);
  }
  if (order?.paymentDetails?.reference) {
    paymentRows.push(["Référence paiement :", order.paymentDetails.reference]);
  }

  doc.setFontSize(9);
  paymentRows.forEach(([label, value]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.gray);
    doc.text(String(label), marginX, cursorY - 4);
    doc.setTextColor(...colors.dark);
    doc.text(cleanSpaces(String(value ?? "-")), marginX + 140, cursorY - 4);
    cursorY += 14;
  });

  // ===== Pied de page =====
  const footerY = pageHeight - 40;
  doc.setDrawColor(...colors.border);
  doc.setLineWidth(0.8);
  doc.line(marginX, footerY - 18, pageWidth - marginX, footerY - 18);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...colors.accent);
  doc.text("Merci pour votre confiance !", pageWidth / 2, footerY, {
    align: "center",
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...colors.gray);
  doc.text(
    `Document généré automatiquement le ${formatDate(Date.now())} par le système ${BRAND.name}`,
    pageWidth / 2,
    footerY + 13,
    { align: "center" }
  );

  return doc;
}

// ---------------------------------------------------------------------
// Actions publiques : téléchargement et impression
// ---------------------------------------------------------------------
export function downloadReceiptPDF(data) {
  const doc = buildReceiptPDF(data);
  doc.save(getReceiptFileName(data?.order));
}

export function printReceiptPDF(data) {
  const doc = buildReceiptPDF(data);
  doc.autoPrint();
  const url = doc.output("bloburl");
  window.open(String(url), "_blank");
}
